/* eslint-disable no-unused-vars -- enums */
import {
  pingSession as pingSessionService,
  signOut as signOutService,
  refreshToken as refreshTokenService,
  SessionType,
  authsvsSignOut,
} from '@egw/api'
import { Cookies, ErrorCodes, setAccessToken, type GQLError } from '@egw/clients'
import { configClient } from '@egw/config'
import { type FeatureFlags } from '@egw/constants'
import { log } from '@egw/logger'
import { useFlags } from '@egw/feature-flags'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type FC,
  type PropsWithChildren,
} from 'react'
import { useMountedState } from 'react-use'
import { signInWithPasskey as signInWithPasskeyService, type PasskeyError } from '../../services'

/**
 * Default ping session interval for non-JWT auth (fallback)
 */
const PING_SESSION_INTERVAL = 4.5 * 60 * 1000

/**
 * Calculates ping session interval based on access token expiration
 * Refreshes 20 seconds before token expires, with a minimum of 5 seconds
 * @param expiresAt - Token expiration time as ISO string
 * @returns Interval in milliseconds
 */
const getPingSessionInterval = (expiresAt: string | undefined): number => {
  if (!expiresAt) {
    return PING_SESSION_INTERVAL
  }

  const expirationTime = new Date(expiresAt).getTime()
  const now = Date.now()
  const timeUntilExpiration = expirationTime - now
  const buffer = 20 * 1000 // Refresh 20 seconds before expiration

  // Calculate interval: time until expiration minus buffer
  const interval = Math.max(5 * 1000, timeUntilExpiration - buffer)

  return interval || PING_SESSION_INTERVAL
}

/**
 * The Auth state property been injected to a component using withAuth HOC.
 */
export interface AuthState {
  isLoading: boolean
  isAuthenticated?: boolean
  userId?: string
  sSessionId?: string
  pSessionId?: string
  accessToken?: string
  expiresAt?: string
  error?: GQLError | PasskeyError | Error
  signInWithPasskey: (email?: string, otpChallengeCb?: () => void) => Promise<void>
  signOut: () => Promise<void>
  authenticateWithUserId: (userId: string) => void
  pingSession: () => Promise<void>
  refreshToken: () => Promise<void>
}

const initialState: AuthState = {
  isLoading: true,
  isAuthenticated: false,
  // eslint-disable-next-line @typescript-eslint/require-await
  signInWithPasskey: async () => {
    log.error('AuthProvider is not implemented')
  },
  // eslint-disable-next-line @typescript-eslint/require-await
  signOut: async () => {
    log.error('AuthProvider is not implemented')
  },
  authenticateWithUserId: () => {
    log.error('AuthProvider is not implemented')
  },
  // eslint-disable-next-line @typescript-eslint/require-await
  pingSession: async () => {
    log.error('AuthProvider is not implemented')
  },
  // eslint-disable-next-line @typescript-eslint/require-await
  refreshToken: async () => {
    log.error('AuthProvider is not implemented')
  },
}

const AuthContext = createContext(initialState)

enum DispatchTypes {
  SET_SESSION,
  RESET_SESSION,
  SET_USER,
  SET_ERROR,
}

interface DispatchProps {
  type: DispatchTypes
  payload: unknown
}

interface SetSessionPayload {
  sSessionId: string
  pSessionId: string
  userId?: string
  accessToken?: string
  expiresAt?: string
  sessionType?: SessionType
}

const reducer = (state: AuthState, { type, payload }: DispatchProps): AuthState => {
  if (type === DispatchTypes.SET_SESSION) {
    const { sSessionId, pSessionId, userId, accessToken, expiresAt, sessionType } = payload as SetSessionPayload
    return {
      ...state,
      sSessionId,
      pSessionId,
      userId,
      accessToken,
      expiresAt,
      isAuthenticated: Boolean(userId) || sessionType === SessionType.AUTHENTICATED,
      isLoading: false,
      error: undefined,
    }
  }
  if (type === DispatchTypes.RESET_SESSION) {
    return {
      ...state,
      isAuthenticated: false,
      userId: undefined,
      error: undefined,
    }
  }
  if (type === DispatchTypes.SET_ERROR) {
    return {
      ...state,
      error: payload as GQLError,
      isLoading: false,
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (type === DispatchTypes.SET_USER) {
    return {
      ...state,
      userId: payload as string | undefined,
      isAuthenticated: Boolean(payload),
      isLoading: false,
      error: undefined,
    }
  }
  return state
}

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [authState, dispatch] = useReducer(reducer, initialState)
  const { userId, isAuthenticated } = authState
  const config = configClient.getConfig() as { graphql: { accessToken: string } }
  const { flags, isLoading: isFlagsLoading } = useFlags<FeatureFlags>()

  // Is mounted component validation functionality, used to avoid memory leak for async operations
  const isMounted = useMountedState()

  useEffect(() => {
    if (userId) {
      notifications.dispatch(NOTIFICATIONS.AUTH.AUTHENTICATED, { userId })
    }
  }, [userId])

  /**
   * Ping session to get session ids and userId if is authenticated
   */
  const pingSession = useCallback(async (): Promise<void> => {
    try {
      const response = await pingSessionService()
      // Get session ids from cookies been set by the server
      const sSessionId = Cookies.get('s-session-id') as string
      const pSessionId = Cookies.get('p-session-id') as string

      // Update state and send notification only if session ids or userId got updated in order to avoid unnecessary re-renders and third-party services updates
      if (
        authState.pSessionId !== pSessionId ||
        authState.sSessionId !== sSessionId ||
        authState.userId !== response.userId
      ) {
        // Dispatch event
        notifications.dispatch(NOTIFICATIONS.AUTH.SESSION_LOADED, {
          sSessionId,
          pSessionId,
          userId: response.userId,
        })
        // Update state
        dispatch({
          type: DispatchTypes.SET_SESSION,
          payload: {
            sSessionId,
            pSessionId,
            userId: response.userId,
          },
        })
      } else if (authState.isLoading) {
        // This statement handles page load
        dispatch({
          type: DispatchTypes.SET_SESSION,
          payload: {
            sSessionId,
            pSessionId,
            userId: response.userId,
          },
        })
      }
    } catch (error) {
      dispatch({ type: DispatchTypes.SET_ERROR, payload: error })
      notifications.dispatch(NOTIFICATIONS.AUTH.SESSION_ERROR, {
        error,
      })
    }
  }, [authState.pSessionId, authState.sSessionId, authState.userId, authState.isLoading])

  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      const response = await refreshTokenService()
      setAccessToken(`Bearer ${response.accessToken}`)

      // Always dispatch event to update HTTP client with new access token
      notifications.dispatch(NOTIFICATIONS.AUTH.SESSION_LOADED, {
        userId: response.userId,
        accessToken: response.accessToken,
        sessionType: response.sessionType,
      })

      // Update state and send notification only if session ids or userId got updated in order to avoid unnecessary re-renders and third-party services updates
      if (authState.userId !== response.userId) {
        // Update state
        dispatch({
          type: DispatchTypes.SET_SESSION,
          payload: {
            userId: response.userId,
            accessToken: response.accessToken,
            expiresAt: response.expiresAt,
            sessionType: response.sessionType,
          },
        })
      } else if (authState.isLoading) {
        // This statement handles page load
        dispatch({
          type: DispatchTypes.SET_SESSION,
          payload: {
            userId: response.userId,
            accessToken: response.accessToken,
            expiresAt: response.expiresAt,
            sessionType: response.sessionType,
          },
        })
      }
    } catch (error) {
      dispatch({ type: DispatchTypes.SET_ERROR, payload: error })
      notifications.dispatch(NOTIFICATIONS.AUTH.SESSION_ERROR, {
        error,
      })
    }
  }, [authState.userId, authState.isLoading])

  useEffect(() => {
    let refreshSessionInterval: ReturnType<typeof setInterval>
    if (!isFlagsLoading) {
      // Ping session on page load
      void refreshToken()

      // Calculate dynamic interval based on access token expiration
      const interval = getPingSessionInterval(authState.expiresAt)

      // Silent refresh session
      refreshSessionInterval = setInterval(() => {
        if (isMounted()) {
          void refreshToken()
        }
      }, interval)
    }
    return () => {
      clearInterval(refreshSessionInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pingSession should only run once or inside of the time interval
  }, [isFlagsLoading, authState.expiresAt])

  // Handle unauthenticated GraphQL response
  useEffect(() => {
    const graphqlErrorHandler = (data: unknown): void => {
      const error = (data as { detail: GQLError }).detail
      if ((error.code as ErrorCodes) === ErrorCodes.UNAUTHENTICATED) {
        void signOut()
      }
    }
    notifications.on(NOTIFICATIONS.CLIENTS.GRAPHQL.ERROR, graphqlErrorHandler)
    return () => {
      notifications.remove(NOTIFICATIONS.CLIENTS.GRAPHQL.ERROR, graphqlErrorHandler)
    }
  }, [])

  // Listen for customer details and store email in localStorage when authenticated
  useEffect(() => {
    const customerDetailsListener = (data: unknown): void => {
      const eventData = data as { detail: { customer: { email: string } } }
      if (isAuthenticated && eventData.detail.customer.email) {
        localStorage.setItem('lastSignInEmail', eventData.detail.customer.email)
      }
    }
    notifications.on(NOTIFICATIONS.API.CUSTOMER_DETAILS, customerDetailsListener)
    return () => {
      notifications.remove(NOTIFICATIONS.API.CUSTOMER_DETAILS, customerDetailsListener)
    }
  }, [isAuthenticated])

  /**
   * Sign in with passkey
   */
  const signInWithPasskey = async (email?: string, otpChallengeCb?: () => void): Promise<void> => {
    try {
      const response = await signInWithPasskeyService(email, otpChallengeCb)
      await refreshToken()
      // Dispatch event
      notifications.dispatch(NOTIFICATIONS.AUTH.SIGN_IN_WITH_PASSKEY_SUCCESS, {
        userId: response.customerId ?? response.userId,
      })
      // Update state (this will trigger isAuthenticated to become true)
      dispatch({ type: DispatchTypes.SET_USER, payload: response.customerId ?? response.userId })
    } catch (error) {
      // Dispatch event
      notifications.dispatch(NOTIFICATIONS.AUTH.SIGN_IN_WITH_PASSKEY_ERROR, { error })
      // Update state
      throw error
    }
  }

  /**
   * Sign in OTP Challenge
   */

  const authenticateWithUserId = (userIdPayload: string): void => {
    // Dispatch event upon updating userId in state, a Notification is then dispatched for other services to listen to Authentication event
    dispatch({ type: DispatchTypes.SET_USER, payload: userIdPayload })
  }

  /**
   * Sign out - sets userId to undefined and makes back-end call to make session unauthenticated
   */
  const signOut = async (): Promise<void> => {
    // Regardless the back-end call we need to reset the session
    notifications.dispatch(NOTIFICATIONS.AUTH.SIGN_OUT) // Dispatch event
    dispatch({ type: DispatchTypes.RESET_SESSION, payload: undefined }) // Update state
    // Make a back-end call to expire cookies
    try {
      await authsvsSignOut()
      await refreshToken()
    } catch (error) {
      dispatch({ type: DispatchTypes.SET_ERROR, payload: error })
    }
  }

  const providerValue = useMemo(
    () => ({
      ...authState,
      signInWithPasskey,
      signOut,
      authenticateWithUserId,
      pingSession,
      refreshToken,
    }),
    [pingSession, refreshToken, authState],
  )
  return <AuthContext.Provider value={providerValue}>{children}</AuthContext.Provider>
}

/**
 * HOC to provide a AuthContext
 * @returns JSX.Element
 * @example export default compose(withAuthProvider)(App)
 */
export const withAuthProvider = (Component: FC) =>
  function WithAuthProvider() {
    return (
      <AuthProvider>
        <Component />
      </AuthProvider>
    )
  }

interface AuthContextProps {
  authState: AuthState
}

/**
 * HOC to consume a Auth Context. Injects authState property into component's properties.
 * Use it with any component under the component level where withAuthProvider was used.
 * @returns JSX.Element
 * @example export default withAuth(MyComponent)
 */
export const withAuth =
  <P extends AuthContextProps>(condition: (_authState: AuthState) => boolean) =>
  (Component: FC<P>) =>
    function WithAuth(props: P) {
      return (
        <AuthContext.Consumer>
          {(context) => (condition(context) ? <Component {...props} authState={context} /> : null)}
        </AuthContext.Consumer>
      )
    }

// Custom hook to access the auth object
export const useAuth = (): AuthState => useContext(AuthContext)

/**
 * HOC condition to return nested component when auth was loaded
 * @returns JSX.Element
 * @example export default withAuth(isLoaded)(MyComponent)
 */
export const isLoaded = (authState: AuthState): boolean => !authState.isLoading

/**
 * HOC condition to return nested component when user is authenticated
 * @returns JSX.Element
 * @example export default withAuth(isAuthenticated)(MyComponent)
 */
export const isAuthenticated = (authState: AuthState): boolean => !authState.isAuthenticated

/**
 * HOC condition to always return nested component
 * @returns JSX.Element
 * @example export default withAuth(always)(MyComponent)
 */
export const always = (): boolean => true
